//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { convertStylesToStyled , copyToClipboard, storeFile } from '@/_utils/utils';
import { Button, Grid, Card, CardContent, Typography, List } from '@mui/material';
import {
  selectJob,
  selectJobRelatedFiles,
  selectJobRelatedFilesIsLoading,
  selectJobRelatedFilesCount,
  selectJobRelatedFilesPagination
} from '@/redux/selectors/jobSelectors';
import axios from '@/_utils/axios';
import baseAxios from 'axios';
import NoData from '@/components/Dashboard/NoData';
import MissingFiles from '@/components/_common/Draw/MissingFiles';
import Spinner from '@/components/_common/Spinner';
import { useToast } from '@/components/_common/Toast';
import ListHeader from './JobRelatedFiles/ListHeader';
import FilesAsGrid from './JobRelatedFiles/FilesAsGrid';
import FilesAsList from './JobRelatedFiles/FilesAsList';
import { FileFormat, ViewTypes } from '@/constants/constants';
import { JobDetailInventoryFile } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import ListFooter from '@/components/_common/Table/TableFooter/TableFooter';
import ListCounts from '@/components/_common/Table/TableFooter/TableCounts';
import ListPagination from '@/components/_common/Table/TableFooter/TablePagination';
import ListPageSize from '@/components/_common/Table/TableFooter/TablePageSize';
import JobRelatedFilePreview from './JobRelatedFiles/JobRelatedFilePreview';

interface JobTabFilesProps {
  showAllFiles: boolean;
  toggleShowAllFiles: () => void;
  pagination: { pageSize: number; pageNum: number; textFiltered?: boolean; };
  updatePagination: (newPagination: { pageSize: number; pageNum: number; textFiltered?: boolean; }) => void;
}

const JobTabFiles = ({ showAllFiles, toggleShowAllFiles, pagination, updatePagination }: JobTabFilesProps) => {
  const { id } = useAppSelector(selectJob);
  const relatedFiles = useAppSelector(selectJobRelatedFiles);
  const relatedFilesLoading = useAppSelector(selectJobRelatedFilesIsLoading);
  const relatedFilesCount = useAppSelector(selectJobRelatedFilesCount);
  const relatedFilesPagination = useAppSelector(selectJobRelatedFilesPagination);
  const [copying, setCopying] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [assetFile, setAssetFile] = useState<{
    name: string;
    label?: string;
    url?: string;
    type?: FileFormat;
  }>();
  const [viewType, setViewType] = useState(ViewTypes.grid);
  const [fileFilter, setFileFilter] = useState('');
  const { success, error } = useToast();

  const filteredRelatedFiles = useMemo(() => {
    if (!fileFilter || !pagination.textFiltered) {
      return {
        list: relatedFiles,
        total: relatedFilesPagination.total,
      };
    }

    const filtered = relatedFiles.filter((file: JobDetailInventoryFile) => {
      const file_type = file?.file_type?.toLowerCase();
      const label = file?.label?.toLowerCase();
      const name = file?.name?.toLowerCase();
      const filter = fileFilter.toLowerCase();
      return name.includes(filter) || label.includes(filter) || file_type.includes(filter);
    });

    const list = filtered.slice((pagination.pageNum - 1) * pagination.pageSize, pagination.pageNum * pagination.pageSize);

    return {
      list,
      total: filtered.length,
    }
  }, [relatedFiles, fileFilter, pagination]);

  if (!id) return null;

  const onFileFilterChange = (newValue: string) => {
    setFileFilter(newValue);
    if (newValue.trim()) {
      updatePagination({ pageNum: 1, pageSize: pagination.pageSize, textFiltered: true })
    } else {
      updatePagination({ pageNum: 1, pageSize: pagination.pageSize, textFiltered: false })
    }
  };

  const handleDownload = (file: JobDetailInventoryFile) => {
    setDownloading(file.name);
    axios
      .get(`/core/jobs/${id}/rally_inventory/${file.label}/download/${file.source_id ? `?source_id=${file.source_id}` : ''}`)
      .then((res) => {
        const url = res?.data?.data?.uri;
        if (url) {
          baseAxios({ url, method: 'GET', responseType: 'blob' }).then((res) => downloadFile(res, file.name));
        }
      })
      .catch(() => error('There was a problem downloading the file.'))
      .then(() => setDownloading(null));
  };

  const copyDownloadLink = (file: JobDetailInventoryFile) => {
    setCopying(file.label);
    axios
      .get(`/core/jobs/${id}/rally_inventory/${file.label}/download/`)
      .then((res) => {
        const url = res?.data?.data?.uri;
        copyToClipboard(url);
        success('Download link copied to clipboard!');
      })
      .catch(() => error('There was a problem copying the download link.'))
      .then(() => setCopying(null));
  };

  const onPreview = (file: JobDetailInventoryFile) => {
    setAssetFile({ name: file.name });
    axios
      .get(`/core/jobs/${id}/rally_inventory/${file.label}/download/${file.source_id ? `?source_id=${file.source_id}` : ''}`)
      .then((res) => {
        const url: string | undefined = res?.data?.data?.uri;

        if (url) {
          const type = (file.file_type.startsWith('.') ? file.file_type.substring(1, file.file_type.length) : file.file_type) as FileFormat;
          setAssetFile({
            url,
            type,
            name: file.name,
            label: file.label,
          });
        }
      })
      .catch(() => {
        error('There was a problem retrieving the file.');
        setAssetFile(undefined);
      });
  };

  const downloadFile = (response: any, filename: string) => {
    storeFile(response, filename);
    setDownloading(null);
  };

  return (
    <>
      <div>
        <ListHeader
          fileFilter={fileFilter}
          onFileFilterChange={onFileFilterChange}
          viewType={viewType}
          changeViewType={setViewType}
          relatedFilesLoading={relatedFilesLoading}
          showAllFiles={showAllFiles}
          toggleShowAllFiles={toggleShowAllFiles}
          relatedFilesCount={filteredRelatedFiles.list.length}
        />
      </div>
      {relatedFilesLoading ? (
        <Spinner style={{ position: 'relative', marginTop: 50, marginBottom: 50 }} />
      ) : (
        <StyledList sx={{ mt: 3, backgroundColor: 'transparent' }}>
          {viewType === ViewTypes.grid && (
            <FilesAsGrid
              files={filteredRelatedFiles.list}
              copyDownloadLink={copyDownloadLink}
              handleDownload={handleDownload}
              onPreviewFile={onPreview}
              copying={copying}
              downloading={downloading}
              preparingPreview={assetFile}
            />
          )}
          {viewType === ViewTypes.list && (
            <FilesAsList
              files={filteredRelatedFiles.list}
              copyDownloadLink={copyDownloadLink}
              handleDownload={handleDownload}
              onPreviewFile={onPreview}
              copying={copying}
              downloading={downloading}
              preparingPreview={assetFile}
            />
          )}
        </StyledList>
      )}
      {!relatedFilesLoading && relatedFilesCount === 0 && (
        <Card elevation={0}>
          <CardContent>
            <NoData>
              <Grid container flexDirection="column" alignItems="center" justifyContent="center" sx={{ p: 4, py: 6 }}>
                <MissingFiles style={{ maxHeight: 150 }} />
                <Typography variant="subtitle2" sx={{ mt: 3 }}>
                  {showAllFiles ? 'No files found.' : 'No job-specific files found.'}
                </Typography>
                {!showAllFiles && (
                  <Button sx={{ mt: 1 }} variant="outlined" color="primary" onClick={() => toggleShowAllFiles()}>
                    View All Files
                  </Button>
                )}
              </Grid>
            </NoData>
          </CardContent>
        </Card>
      )}
      {!relatedFilesLoading && relatedFilesCount > 0 && (
        <ListFooter container sx={{ paddingTop: '1rem' }}>
          <Grid item>
            <ListCounts
              currentPage={pagination.pageNum}
              pageSize={pagination.pageSize}
              totalCount={pagination.textFiltered ? filteredRelatedFiles.total : relatedFilesPagination.total}
              tableKey="relatedFiles"
            />
          </Grid>
          {(relatedFilesPagination.total > pagination.pageSize || pagination.textFiltered ) && (
            <PaginationWrapper item>
              <ListPagination
                currentPage={pagination.pageNum}
                pageSize={pagination.pageSize}
                totalCount={pagination.textFiltered ? filteredRelatedFiles.total : relatedFilesPagination.total}
                onPageChange={(newPageNum) => updatePagination({ ...pagination, pageNum: newPageNum })}
              />
            </PaginationWrapper>
          )}
          <PageSizeWrapper item>
            <ListPageSize
              currentPageSize={pagination.pageSize}
              onPageSizeChange={(newPageSize) => updatePagination({ ...pagination, pageSize: newPageSize })}
            />
          </PageSizeWrapper>
        </ListFooter>
      )}
      <JobRelatedFilePreview onClose={() => setAssetFile(undefined)} file={assetFile} />
    </>
  );
};

export default JobTabFiles;

const StyledList = styled(List)`
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.LoadingBar)}
`;

const PaginationWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;

const PageSizeWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;
